/**
 * SEO Component
 *
 * Manages document head meta tags and structured data.
 * Uses admin-configured SEO settings.
 */

import { useEffect } from 'react';
import { useSEOSettings } from '../contexts/SettingsContext';
import type { Product } from '@petmas/shared';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  noIndex?: boolean;
}

interface ProductSEOProps extends SEOProps {
  product: Product;
  breadcrumbs?: BreadcrumbItem[];
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Base SEO component for meta tags
 */
export function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noIndex = false,
}: SEOProps) {
  const settings = useSEOSettings();

  const fullTitle = title ? `${title} | ${settings.siteName}` : settings.siteName;
  const metaDescription = description ?? settings.siteDescription;
  const metaImage = ogImage ?? settings.defaultOgImage;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set or remove meta tag
    const setMeta = (name: string, content: string | undefined, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);

      if (content) {
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      } else if (meta) {
        meta.remove();
      }
    };

    // Helper to set or remove link tag
    const setLink = (rel: string, href: string | undefined) => {
      let link = document.querySelector(`link[rel="${rel}"]`);

      if (href) {
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      } else if (link) {
        link.remove();
      }
    };

    // Basic meta tags
    setMeta('description', metaDescription);
    setMeta('robots', noIndex ? 'noindex,nofollow' : undefined);

    // Canonical URL
    setLink('canonical', canonical);

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', metaDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', metaImage, true);
    setMeta('og:site_name', settings.siteName, true);
    if (canonical) {
      setMeta('og:url', canonical, true);
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', metaDescription);
    setMeta('twitter:image', metaImage);

    // Cleanup function
    return () => {
      // Don't remove meta tags on cleanup - they'll be updated by the next page
    };
  }, [fullTitle, metaDescription, metaImage, canonical, ogType, noIndex, settings.siteName]);

  return null;
}

/**
 * Product SEO with structured data
 */
export function ProductSEO({ product, breadcrumbs = [], ...seoProps }: ProductSEOProps) {
  const settings = useSEOSettings();

  // Get first variant for stock/sku info, or calculate total stock
  const firstVariant = product.variants?.[0];
  const totalStock = product.variants?.reduce((sum, v) => sum + (v.stockAvailable ?? 0), 0) ?? 0;

  // Product availability mapping
  const getAvailability = () => {
    if (totalStock > 0) {
      return 'https://schema.org/InStock';
    }
    return 'https://schema.org/OutOfStock';
  };

  // Build Product schema
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description ?? seoProps.description,
    image: product.imageUrl ? [product.imageUrl] : [],
    sku: firstVariant?.sku ?? product.id,
    brand: product.brand
      ? {
          '@type': 'Brand',
          name: product.brand,
        }
      : undefined,
    offers: {
      '@type': 'Offer',
      url: seoProps.canonical,
      priceCurrency: 'PEN',
      price: product.price,
      availability: getAvailability(),
      seller: {
        '@type': 'Organization',
        name: settings.organization.name,
      },
    },
  };

  // Build BreadcrumbList schema
  const breadcrumbSchema =
    settings.schemaBreadcrumbEnabled && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        }
      : null;

  useEffect(() => {
    if (!settings.schemaProductEnabled) return;

    // Add Product schema
    const productScriptId = 'schema-product';
    let productScript = document.getElementById(productScriptId) as HTMLScriptElement | null;

    if (!productScript) {
      productScript = document.createElement('script');
      productScript.id = productScriptId;
      productScript.type = 'application/ld+json';
      document.head.appendChild(productScript);
    }
    productScript.textContent = JSON.stringify(productSchema);

    // Add Breadcrumb schema
    const breadcrumbScriptId = 'schema-breadcrumb';
    let breadcrumbScript = document.getElementById(breadcrumbScriptId) as HTMLScriptElement | null;

    if (breadcrumbSchema) {
      if (!breadcrumbScript) {
        breadcrumbScript = document.createElement('script');
        breadcrumbScript.id = breadcrumbScriptId;
        breadcrumbScript.type = 'application/ld+json';
        document.head.appendChild(breadcrumbScript);
      }
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    } else if (breadcrumbScript) {
      breadcrumbScript.remove();
    }

    return () => {
      // Clean up schemas when component unmounts
      document.getElementById(productScriptId)?.remove();
      document.getElementById(breadcrumbScriptId)?.remove();
    };
  }, [product, breadcrumbSchema, settings.schemaProductEnabled]);

  return (
    <SEO
      title={product.name}
      description={product.description ?? `Compra ${product.name} en PetMas. ${product.brand ? `Marca: ${product.brand}.` : ''} Envio a todo Peru.`}
      ogType="product"
      ogImage={product.imageUrl ?? undefined}
      {...seoProps}
    />
  );
}

/**
 * Organization schema (added once to the app)
 */
export function OrganizationSchema() {
  const settings = useSEOSettings();

  useEffect(() => {
    if (!settings.schemaOrganizationEnabled) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: settings.organization.name,
      url: typeof window !== 'undefined' ? window.location.origin : '',
      logo: settings.defaultOgImage,
      description: settings.siteDescription,
      address: {
        '@type': 'PostalAddress',
        streetAddress: settings.organization.address.street,
        addressLocality: settings.organization.address.city,
        addressRegion: settings.organization.address.region,
        postalCode: settings.organization.address.postalCode,
        addressCountry: settings.organization.address.country,
      },
      telephone: settings.organization.phone,
      email: settings.organization.email,
      openingHours: settings.organization.hours,
      sameAs: [
        settings.organization.social.facebook,
        settings.organization.social.instagram,
        settings.organization.social.twitter,
        settings.organization.social.youtube,
      ].filter(Boolean),
    };

    const scriptId = 'schema-organization';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [settings]);

  return null;
}

/**
 * Breadcrumb component with schema
 */
export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  const settings = useSEOSettings();

  useEffect(() => {
    if (!settings.schemaBreadcrumbEnabled || items.length === 0) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    const scriptId = 'schema-breadcrumb';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [items, settings.schemaBreadcrumbEnabled]);

  // Visual breadcrumb
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
      <ol className="flex items-center">
        {items.map((item, index) => (
          <li key={item.url} className="flex items-center">
            {index > 0 && <span className="mx-2">/</span>}
            {index === items.length - 1 ? (
              <span className="text-gray-900">{item.name}</span>
            ) : (
              <a href={item.url} className="hover:text-orange-500">
                {item.name}
              </a>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
